import _ from 'lodash';
import { gql } from '@apollo/client';
import REVENUE_METRICS from '../constants/revenueMetrics';
import { useDataTransforms, useFarmName, useFilterYears } from '../utils/hooks';
import useFarmOrSectionGroupData from '../hooks/useFarmOrSectionGroupData';
import { useUnitsMap } from '../contexts/UnitContext';
import ItemsTable from '../components/tables/ItemsTable';
import { max } from '../utils/aggregators';
import { metricsQueryString } from '../utils/utils';
import { asCurrency } from '../utils/formatters';
import MetricsSummary from '../components/economics/MetricsSummary';
import { colorForCrop } from '../utils/colors';
import MetricsHeader from '../components/layout/MetricsHeader';
import DetailsSection from '../components/layout/DetailsSection';
import SummarySection from '../components/layout/SummarySection';
import Content from '../components/layout/Content';
import { CropDisplayFragment } from '../queries';
import { INVERTED_REVENUE_UNITS_BY_DEFAULT } from '../constants/units';
import { getRevenueStreamData } from '../utils/transforms';

const ALL_CROPS = 'All Crops';
const QUERY_NAME = 'RevenueEconQuery';
const FRAGMENT_NAME = 'RevenueEconFragment';

const RevenueFragment = gql`
  ${CropDisplayFragment}
  fragment ${FRAGMENT_NAME} on SectionsAggregate {
    economicSummary {
      revenueByYear
    }
    cropAggregates {
      crop {
        ...CropDisplayFields
      }
      economicSummary {
        revenueByYear
      }
      revenueStreamAggregates {
        revenueByYear
        revenueStream {
          id
          unit
          ${metricsQueryString(REVENUE_METRICS)}
        }
      }
    }
  }
`;

const toSummaryData = ({ cropAggregates, economicSummary }) => ({
  ..._.fromPairs(
    cropAggregates.map(
      ({ crop: { name }, economicSummary: { revenueByYear } }) => [
        name,
        revenueByYear,
      ],
    ),
  ),
  [ALL_CROPS]: economicSummary.revenueByYear,
});

const getSummaryMetrics = ({ cropAggregates }) =>
  [{ crop: { name: ALL_CROPS, isAggregate: true } }, ...cropAggregates].map(
    ({ crop }) => ({
      displayName: crop.name,
      accessor: crop.name,
      isAggregate: crop.isAggregate,
      aggregator: max,
      color: colorForCrop(crop),
      defaultShown: true,
      format: asCurrency,
      info: '(Max/yr)',
      yAxisId: 'currency',
    }),
  );

const RevenueView = () => {
  const filterYears = useFilterYears();
  const farmName = useFarmName();

  const { msg, data } = useFarmOrSectionGroupData(
    RevenueFragment,
    QUERY_NAME,
    FRAGMENT_NAME,
  );

  const invertedRevenueUnitMap = useUnitsMap(INVERTED_REVENUE_UNITS_BY_DEFAULT);

  const [summaryData = {}, summaryMetrics] = useDataTransforms(data, [
    toSummaryData,
    getSummaryMetrics,
  ]);

  return (
    <>
      <MetricsHeader showCumulativeYearSelect showAnnualSingleYearSelect>
        Revenue
      </MetricsHeader>
      <Content>
        <SummarySection>
          <MetricsSummary
            metrics={summaryMetrics}
            data={summaryData}
            arrayTransform={filterYears}
            msg={msg}
          />
        </SummarySection>
        <DetailsSection>
          <ItemsTable
            data={data}
            cols={REVENUE_METRICS}
            msg={msg}
            getItems={getRevenueStreamData(invertedRevenueUnitMap)}
            byCrop
            tableHeader={<h3>Revenue Breakdown</h3>}
            csvFileName={['Revenue', farmName].join(' - ')}
          />
        </DetailsSection>
      </Content>
    </>
  );
};

export default RevenueView;
